import { getWebPath } from '@doss/common-all'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { BreadcrumbObjectType, DataType } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import {
  MinimalLookupTableRowCell,
  resolveLookupEnumRendering,
} from '~/components/records/lookup/hooks/LookupProvider'
import { EnumChip } from '~/components/shared/EnumChip'
import { EnumOptionMark } from '~/components/shared/EnumOptionMark'
import { LookupEnumSource } from '~/components/shared/lookup-enum'

interface LookupCellChipV2Props {
  textSize?: 'xs' | 'sm'
  allowNavigate: boolean
  disabled?: boolean
  className?: string
  type?: 'text' | 'chip'
  onDelete?: () => void
  /** The source enum that styles this lookup's value. Omit when the source column isn't an enum */
  enumSource?: {
    /** The enum on the source column, shared by every cell in it */
    column: LookupEnumSource | null
    /**
     * The cell this lookup reads. Only used when `column` is a multi-select, where `lookup.value` is
     * the joined display string and each underlying value needs its own chip.
     */
    cell?: MinimalLookupTableRowCell
  }
  lookup: {
    value: string
    id?: string
    rowId?: string
    column?: {
      type: DataType
    }
    documents?: { id: string }[]
  }
}

/**
 * Renders a chip with a document name and download link
 */
export function LookupCellChipV2({
  disabled = false,
  lookup,
  textSize = 'xs',
  type = 'text',
  allowNavigate,
  className,
  onDelete,
  enumSource,
}: LookupCellChipV2Props) {
  const { t } = useTypedTranslation()
  // A chip per linked record is how a multi lookup renders, so `type` is also its cardinality
  const enumRendering = resolveLookupEnumRendering({
    enumSource: enumSource?.column,
    label: lookup.value,
    cell: enumSource?.cell,
    isArray: type === 'chip',
  })
  const enumChips = enumRendering?.kind === 'chips' ? enumRendering.values : null
  const enumMark = enumRendering?.kind === 'text' ? enumRendering : null
  const showsEnumChips = (enumChips?.length ?? 0) > 0
  const noValue = !showsEnumChips && lookup.value === ''

  const href = (() => {
    if (!allowNavigate) {
      return undefined
    }
    if (lookup.column && lookup.column.type === DataType.FILE_URL) {
      return lookup.documents && lookup.documents.length > 0
        ? getWebPath({
            objectType: BreadcrumbObjectType.DOCUMENT,
            objectId: lookup.documents[0].id,
          })
        : undefined
    }
    return getWebPath({ objectType: BreadcrumbObjectType.TABLE_ROW, objectId: lookup.rowId })
  })()

  const label = noValue ? t('lookup.no_value') : lookup.value

  // Derived UI flags. Enum chips carry their own fill, so they sit in the plain container rather than a
  // nested neutral chip
  const isChip = type === 'chip' && !showsEnumChips
  const isDisabled = disabled
  const isLink = Boolean(href)
  const showUnderline = isLink && !isDisabled && allowNavigate
  const showEmptyOnHover = !isChip && noValue
  const showMark = !isChip && !noValue && Boolean(enumMark?.color || enumMark?.iconName)

  // Shared classes (wrapper-only padding)
  const wrapperClasses = cn(
    'select-text rounded-sm whitespace-nowrap transition-colors',
    textSize === 'sm' && 'text-sm',
    (showUnderline || showEmptyOnHover) && 'group/lookup-chip',
    !isChip && 'inline-flex items-center',
    // Matches the gap a native multi-select cell uses, so a lookup at one reads the same as the source
    (showMark || showsEnumChips) && 'gap-2',
    isChip && 'inline-flex items-center gap-1 border bg-gray-2 border-gray-3',
    !showsEnumChips && (isChip ? 'px-1.5 py-0' : 'px-1 py-0'),
    isDisabled ? 'text-gray-9 cursor-default' : 'text-gray-10',
    !isChip && !showsEnumChips && !isDisabled && isLink && 'hover:bg-black/[.04]',
    !isDisabled && isLink && 'hover:cursor-pointer',
    isLink && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-6',
    className,
  )

  const labelClasses = cn(
    'inline-block align-middle',
    showEmptyOnHover && 'text-transparent',
    showEmptyOnHover &&
      (isDisabled ? 'group-hover/lookup-chip:text-gray-9' : 'group-hover/lookup-chip:text-gray-10'),
    showUnderline && 'group-hover/lookup-chip:underline',
  )

  const deleteButtonClasses = cn(
    'shrink-0 ms-1 p-0.5 rounded text-gray-8',
    'transition-colors hover:text-gray-12 focus:outline-none focus:ring-1 focus:ring-gray-6',
  )

  const DeleteButton = () => (
    <button
      aria-label="Remove chip"
      className={deleteButtonClasses}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onDelete && onDelete()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault()
          e.stopPropagation()
          onDelete && onDelete()
        }
      }}
      type="button"
    >
      <X aria-hidden="true" size={12} />
    </button>
  )

  const content =
    showsEnumChips && enumChips ? (
      enumChips.map((enumValue) => (
        <EnumChip
          key={enumValue.label}
          // Underlined together on hover, since the group is one link to one record
          className={cn(
            'shrink-0',
            textSize === 'sm' && 'h-6 py-0 text-sm',
            showUnderline && 'group-hover/lookup-chip:underline',
          )}
          color={enumValue.color}
          iconName={enumValue.iconName}
          label={enumValue.label}
        />
      ))
    ) : (
      <>
        {showMark && <EnumOptionMark color={enumMark?.color} iconName={enumMark?.iconName} />}
        <span className={labelClasses}>{label}</span>
      </>
    )

  if (isLink) {
    return (
      <a
        className={wrapperClasses}
        href={href}
        // Do not open the picker when clicking a chip to open the source row
        onClick={(e) => e.stopPropagation()}
        rel="noopener noreferrer"
        target="_blank"
      >
        {content}
        {isChip && onDelete && <DeleteButton />}
      </a>
    )
  }

  return (
    <span className={wrapperClasses} title={label}>
      {content}
      {isChip && onDelete && <DeleteButton />}
    </span>
  )
}
